import { action, computed, makeObservable, observable, runInAction } from "mobx";
import i18n from "i18next";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";
import { GET_CUSTOMER_SETTINGS, UPDATE_CUSTOMER_SETTING_VALUE } from "src/api/customer";
import { MessageType } from "src/components/notifications/Notifier";

export interface ICustomerSettingRow {
    key: string;
    value: string | null;
    default: string | null;
    valuetype?: number | null;
}

interface IGetCustomerSettingsData {
    ums_customersettings: ICustomerSettingRow[];
}

interface IGetCustomerSettingsVars {
    customerid: string;
}

interface IUpdateCustomerSettingValueData {
    update_ums_customersettings: {
        affected_rows: number;
    } | null;
}

interface IUpdateCustomerSettingValueVars {
    customerid: string;
    key: string;
    value: string | null;
}

type CustomerSettingsError = Error & { networkError?: Error };

enum CustomerSettingValueType {
    BOOLEAN = 1,
    NUMBER = 2,
    JSON = 4
}

function getError(error: unknown, fallbackMessage: string): CustomerSettingsError {
    return error instanceof Error ? error : new Error(fallbackMessage);
}

function isBooleanValue(value: string) {
    return ["true", "false", "0", "1"].includes(value.trim().toLowerCase());
}

function isBooleanTextValue(value: string) {
    return ["true", "false"].includes(value.trim().toLowerCase());
}

function isBooleanSetting(row: ICustomerSettingRow) {
    const lowerKey = row.key.toLowerCase();

    if (row.valuetype !== null && row.valuetype !== undefined) {
        return row.valuetype === CustomerSettingValueType.BOOLEAN;
    }

    return (
        isBooleanTextValue(row.value ?? "") ||
        isBooleanTextValue(row.default ?? "") ||
        lowerKey.includes("demomode") ||
        lowerKey.includes("enabled") ||
        lowerKey.includes("feature") ||
        lowerKey.includes("send_") ||
        lowerKey.includes("active")
    );
}

function isEmailKey(key: string) {
    const lowerKey = key.toLowerCase();

    if (lowerKey.includes("subject") || lowerKey.includes("body") || lowerKey.includes("template")) {
        return false;
    }

    return (
        lowerKey.endsWith("email") ||
        lowerKey.endsWith("emailaddress") ||
        lowerKey.includes("emailinbox") ||
        lowerKey.includes("recipient") ||
        lowerKey.includes("replyto")
    );
}

function isNumericValue(value: string | null) {
    if (value === null) {
        return false;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 && !Number.isNaN(Number(trimmedValue));
}

function isLikelyNumberSetting(row: ICustomerSettingRow) {
    if (row.valuetype !== null && row.valuetype !== undefined) {
        return row.valuetype === CustomerSettingValueType.NUMBER;
    }

    return [row.value, row.default].some(isNumericValue);
}

function isValidEmailList(value: string) {
    const emails = value
        .split(/[;,]/)
        .map((email) => email.trim())
        .filter(Boolean);

    return emails.length > 0 && emails.every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
}

export class CustomerSettingsStore {
    rootStore: RootStore;

    @observable customerid: string | null = null;
    @observable settings: ICustomerSettingRow[] = [];
    @observable loading = false;
    @observable saving = false;
    @observable loadError: CustomerSettingsError | null = null;
    @observable saveError: CustomerSettingsError | null = null;
    @observable editMode = false;
    @observable editedValues: Record<string, string> = {};
    @observable validationErrors: Record<string, string> = {};

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this);
    }

    @action
    reset = () => {
        this.customerid = null;
        this.settings = [];
        this.loading = false;
        this.saving = false;
        this.loadError = null;
        this.saveError = null;
        this.stopEdit();
    };

    @action
    loadSettings = async (customerid: string) => {
        this.customerid = customerid;
        this.loading = true;
        this.loadError = null;

        try {
            const { data } = await apolloClientInstance.query<IGetCustomerSettingsData, IGetCustomerSettingsVars>({
                query: GET_CUSTOMER_SETTINGS,
                variables: { customerid }
            });

            runInAction(() => {
                this.settings = data.ums_customersettings ?? [];
                this.stopEdit();
            });
        } catch (err: unknown) {
            runInAction(() => {
                this.loadError = getError(err, String(i18n.t("customer_settings.unknown_error")));
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @action
    startEdit = () => {
        if (this.editMode) {
            return;
        }

        this.editMode = true;
        this.editedValues = this.getInitialEditedValues();
        this.validationErrors = {};
    };

    @action
    stopEdit = () => {
        this.editMode = false;
        this.editedValues = {};
        this.validationErrors = {};
    };

    @action
    toggleEditMode = () => {
        if (this.editMode) {
            this.stopEdit();
            return;
        }

        this.startEdit();
    };

    @action
    setEditedValue = (key: string, value: string) => {
        this.editedValues = { ...this.editedValues, [key]: value };
        const row = this.getSetting(key);

        if (row) {
            this.setValidationError(key, this.getValidationError(row, value));
        }
    };

    getInitialEditedValues() {
        return this.settings.reduce<Record<string, string>>((values, row) => {
            values[row.key] = row.value ?? "";
            return values;
        }, {});
    }

    getEditedValue(key: string) {
        return this.editedValues[key] ?? "";
    }

    getSetting(key: string) {
        return this.settings.find((setting) => setting.key === key);
    }

    isDirty(key: string) {
        const original = this.getSetting(key);
        return this.getEditedValue(key) !== (original?.value ?? "");
    }

    @computed
    get dirtyRows() {
        return this.settings.filter((setting) => this.isDirty(setting.key));
    }

    @computed
    get hasDirtyValues() {
        return this.dirtyRows.length > 0;
    }

    getValidationError(row: ICustomerSettingRow, value: string) {
        const trimmedValue = value.trim();

        if (trimmedValue.length === 0) {
            return null;
        }

        if (isBooleanSetting(row)) {
            return isBooleanValue(trimmedValue)
                ? null
                : String(i18n.t("customer_settings.validation_boolean"));
        }

        if (isLikelyNumberSetting(row) && Number.isNaN(Number(trimmedValue))) {
            return String(i18n.t("customer_settings.validation_number"));
        }

        if (row.valuetype === CustomerSettingValueType.JSON) {
            try {
                JSON.parse(trimmedValue);
            } catch {
                return String(i18n.t("customer_settings.validation_json"));
            }
        }

        if (isEmailKey(row.key) && !isValidEmailList(trimmedValue)) {
            return String(i18n.t("customer_settings.validation_email"));
        }

        return null;
    }

    @action
    setValidationError = (key: string, validationError: string | null) => {
        const nextErrors = { ...this.validationErrors };

        if (validationError) {
            nextErrors[key] = validationError;
        } else {
            delete nextErrors[key];
        }

        this.validationErrors = nextErrors;
    };

    @action
    validateRow = (row: ICustomerSettingRow) => {
        const validationError = this.getValidationError(row, this.getEditedValue(row.key));
        this.setValidationError(row.key, validationError);

        if (validationError) {
            this.rootStore.uiStore.printStatusMessage(validationError, MessageType.ERROR);
            return false;
        }

        return true;
    };

    @action
    updateSettingValue = async (key: string, value: string | null, suppressSuccessMessage = false): Promise<boolean> => {
        if (!this.customerid) {
            this.rootStore.uiStore.printStatusMessage(
                String(i18n.t("customer_settings.save_error", { message: i18n.t("customer_settings.unknown_error") })),
                MessageType.ERROR
            );
            return false;
        }

        this.saving = true;
        this.saveError = null;

        try {
            const { data } = await apolloClientInstance.mutate<
                IUpdateCustomerSettingValueData,
                IUpdateCustomerSettingValueVars
            >({
                mutation: UPDATE_CUSTOMER_SETTING_VALUE,
                variables: { customerid: this.customerid, key, value }
            });

            if (!data?.update_ums_customersettings?.affected_rows) {
                throw new Error(String(i18n.t("customer_settings.mutation_no_rows")));
            }

            runInAction(() => {
                this.settings = this.settings.map((setting) => (setting.key === key ? { ...setting, value } : setting));
                this.editedValues = { ...this.editedValues, [key]: value ?? "" };
                this.setValidationError(key, null);
            });
            if (!suppressSuccessMessage) {
                this.rootStore.uiStore.printStatusMessage(
                    String(i18n.t("customer_settings.save_success")),
                    MessageType.INFO
                );
            }
            return true;
        } catch (err: unknown) {
            runInAction(() => {
                this.saveError = getError(err, String(i18n.t("customer_settings.unknown_error")));
            });
            const errorMessage = this.saveError?.message ?? String(i18n.t("customer_settings.unknown_error"));
            this.rootStore.uiStore.printStatusMessage(
                String(i18n.t("customer_settings.save_error", { message: errorMessage })),
                MessageType.ERROR
            );
            return false;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    };

    @action
    saveValue = async (key: string) => {
        const row = this.getSetting(key);

        if (!row || !this.isDirty(key) || this.saving || !this.validateRow(row)) {
            return false;
        }

        const nextValue = this.getEditedValue(key);
        return this.updateSettingValue(key, nextValue.length > 0 ? nextValue : null);
    };

    @action
    saveDirtyValues = async () => {
        const dirtyRows = this.dirtyRows;

        if (dirtyRows.length === 0 || this.saving) {
            return false;
        }

        if (!dirtyRows.every(this.validateRow)) {
            return false;
        }

        let allValuesSaved = true;

        for (const row of dirtyRows) {
            const nextValue = this.getEditedValue(row.key);
            const didSave = await this.updateSettingValue(row.key, nextValue.length > 0 ? nextValue : null, true);

            if (!didSave) {
                allValuesSaved = false;
            }
        }

        if (allValuesSaved) {
            this.rootStore.uiStore.printStatusMessage(
                dirtyRows.length === 1
                    ? String(i18n.t("customer_settings.save_success"))
                    : String(i18n.t("customer_settings.save_all_success")),
                MessageType.INFO
            );
        }

        return allValuesSaved;
    };
}
